import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';

import { logError } from '~/common/utils/log';
import { reportEvent } from '~/domains/analytics';
import { isSplashApiError } from '~/domains/services/utils/isBEApiError';
import { createAeropayDeposit } from '~/services/wallet/wallet.service.api';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';
import { bugSnagLog } from '~/utils/bugsnag';

import { useWalletBalance } from '../../hooks/useWalletBalance';
import { useWalletRefundableBreakdown } from '../../hooks/useWalletRefundableBreakdown';
import { PaymentsQueryKeys } from '../../query';
import { aeropayDeclineMessageKey, readAeropayDecline } from '../utils/aeropayDecline';
import { invalidateDepositBonuses } from '../utils/query-invalidation';

import { isCustomDeposit } from './consts';

export function useCreateAeropayDeposit({
  setCompletedDepositAmount,
}: {
  setCompletedDepositAmount?: (amount: number, response?: IPostWalletsDepositsResponse) => void;
}) {
  const { t } = useTranslation('payments');
  const queryClient = useQueryClient();
  const { refetch: refetchWalletBalance } = useWalletBalance();
  const { refetch: refetchRefundableBreakdown } = useWalletRefundableBreakdown();

  return useMutation({
    mutationKey: [PaymentsQueryKeys.CREATE_AEROPAY_DEPOSIT],
    mutationFn: async ({
      amount,
      bankAccountId,
      depositAmount,
      bonusId,
    }: {
      amount: number;
      bankAccountId: string;
      depositAmount: number;
      bonusId?: string;
    }) => {
      const response = await createAeropayDeposit({
        amount,
        bankAccountId,
        ...(bonusId ? { bonusId } : {}),
      });

      refetchWalletBalance();
      refetchRefundableBreakdown();
      invalidateDepositBonuses(queryClient, bonusId);
      setCompletedDepositAmount?.(depositAmount, response);
      reportEvent('Transactions > Deposit', {
        deposit_method: 'AEROPAY',
        deposit_amount: depositAmount,
        is_custom_deposit: isCustomDeposit(amount),
        currency: 'USD',
        bonus_id: bonusId,
      });
    },
    onError: (error) => {
      const decline = readAeropayDecline(error);
      const declineMessageKey = decline && aeropayDeclineMessageKey(decline);

      if (decline && declineMessageKey) {
        bugSnagLog({
          errorMessage: `Aeropay declined a deposit: ${decline.declineCategory}`,
          context: 'aeropay-deposit',
          severity: 'warning',
          details: decline,
        });
      } else {
        logError(error);
      }

      const fallbackMessage =
        !decline && isSplashApiError(error)
          ? error.response.data.message
          : t('deposits.errors.aeropay.general');

      showNotification({
        title: t('deposits.errors.aeropay.title'),
        message: declineMessageKey ? t(declineMessageKey) : fallbackMessage,
        color: 'red',
        autoClose: 5000,
      });
      throw error;
    },
  });
}
